/**
 * KeyboardShortcutsProvider.jsx
 * Provider para gerenciamento global de atalhos de teclado
 *
 * Features:
 * - Atalhos globais de navegação (G + M, G + V, etc)
 * - Controle do Command Bar (Ctrl+K)
 * - Modal de ajuda (?)
 * - Contexto para componentes filhos registrarem atalhos
 *
 * Uso:
 *   <KeyboardShortcutsProvider>
 *     <App />
 *   </KeyboardShortcutsProvider>
 *
 *   // Em componentes filhos:
 *   const { setCommandBarOpen, setHelpOpen } = useKeyboardShortcutsContext();
 */

import React, {
 createContext, useContext, useState, useCallback, useMemo 
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

// ============================================================================
// CONTEXT
// ============================================================================

const KeyboardShortcutsContext = createContext(null);

/**
 * Hook para acessar o contexto de atalhos
 */
export const useKeyboardShortcutsContext = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider');
  }
  return context;
};

// ============================================================================
// DEFINIÇÃO DE ATALHOS
// ============================================================================

// Atalhos de navegação (G + tecla)
const NAVIGATION_SHORTCUTS = {
  'g m': '/',                           // Mapa (MainPage)
  'g v': '/settings/devices',           // Veículos
  'g d': '/settings/drivers',           // Motoristas
  'g g': '/geofences',                  // Geofences (mapa de geofences)
  'g r': '/reports/combined',           // Relatórios
  'g s': '/settings/preferences',       // Configurações
  'g u': '/settings/users',             // Usuários
  'g n': '/settings/notifications',     // Notificações
  'g f': '/fleet',                      // Fleet Dashboard
};

// ============================================================================
// PROVIDER
// ============================================================================

export const KeyboardShortcutsProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Estado dos modais
  const [helpOpen, setHelpOpen] = useState(false);
  const [commandBarOpen, setCommandBarOpen] = useState(false);

  // Callbacks de navegação
  const handleNavigate = useCallback((path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  }, [navigate, location.pathname]);

  // Construir mapa de atalhos
  const shortcuts = useMemo(() => {
    const map = {};

    // Atalho do Command Bar
    map['ctrl+k'] = () => setCommandBarOpen(true);

    // Atalho de ajuda
    map['?'] = () => setHelpOpen(true);

    // Escape fecha modais
    map['escape'] = () => {
      if (helpOpen) {
        setHelpOpen(false);
      } else if (commandBarOpen) {
        setCommandBarOpen(false);
      }
    };

    // Atalhos de navegação
    Object.entries(NAVIGATION_SHORTCUTS).forEach(([shortcut, path]) => {
      map[shortcut] = () => handleNavigate(path);
    });

    return map;
  }, [handleNavigate, helpOpen, commandBarOpen]);

  // Registrar atalhos
  useKeyboardShortcuts(shortcuts, {
    enabled: true,
    preventDefault: true,
  });

  // Valor do contexto
  const contextValue = useMemo(() => ({
    // Estado dos modais
    helpOpen,
    setHelpOpen,
    commandBarOpen,
    setCommandBarOpen,

    // Atalhos de navegação (para exibição no modal de ajuda)
    navigationShortcuts: NAVIGATION_SHORTCUTS,

    // Método para fechar todos os modais
    closeAll: () => {
      setHelpOpen(false);
      setCommandBarOpen(false);
    },
  }), [helpOpen, commandBarOpen]);

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

export default KeyboardShortcutsProvider;
